import {
  Table,
  Badge,
  Checkbox,
  SearchInput,
  Select,
  Menu,
  MenuButton,
  Button,
  ButtonIcon,
  MenuList,
  MenuItem
} from '@morressier/alembic';
import { Flex, Tooltip, Text, Box } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsis,
  faFileCircleXmark,
  faInfoCircle,
  faMemoCircleCheck,
  faPeopleArrows,
  faSearch,
  faUserTag
} from '@fortawesome/pro-regular-svg-icons';
import { IPeerReviewListItem } from '@publishing-engine/domain/views';
import {
  renderActionsColumn,
  renderDeadlineColumn,
  renderRecommendationColumn,
  renderReviewScoreColumn,
  renderReviewStatusColumn
} from './RevisionTableColumns';
import {
  IPeerReviewWithdrawActions,
  IReviewer
} from '@publishing-engine/domain/models';
import { resolveRoundConsentToReInvite } from './consentToReInvite';
import { useSubmissionReviewsData } from 'src/contexts/SubmissionReviewsDataProvider';
import { getReviewerNameOrEmail } from 'src/utils/reviews';
import { AdHocModalOpen } from '../AuthorsTab/ComposeEmailModal';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAuthorProfileData,
  fetchReviewerStatus
} from '../../../api/queries';
import ProxyUser from 'src/components/ProxyUser/ProxyUser';
import UserName from 'src/components/User/UserName';
import { getOrgId } from 'src/pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import { IAuthor } from '@publishing-engine/domain/types/AuthorType';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';

interface TableColumn {
  key: string;
  label: string;
  size: string | number;
  render?: (
    review: IPeerReviewListItem,
    onSelectedAuthor: (author: IReviewer | null) => void,
    reviewerData?: IAuthor
  ) => React.ReactNode;
}

interface RevisionReviewTableProps {
  reviews: IPeerReviewListItem[];
  onSelectReviewId: (reviewId: string) => void;
  onSelectedAuthor: (author: IReviewer | null) => void;
  onGrantExtension: (reviewId: string) => void;
  onRateReview: (reviewId: string) => void;
  onReinviteReviewer: (reviewId: string) => void;
  onDeclineReviewReason: (reviewId: string) => void;
  onResendInvite: (reviewId: string) => void;
  onWithdrawReviewer: (
    reviewId: string,
    reviewer: IReviewer,
    actionName: IPeerReviewWithdrawActions
  ) => void;
  onSendEmail: (modal: AdHocModalOpen) => void;
  onRescindReview: (reviewId: string) => void;
  onReassignBoardMember?: (reviewId: string) => void;
}

export const ConsentToReInviteIcon: React.FC<{
  consentToReInvite?: boolean;
  currentState: string;
}> = ({ consentToReInvite, currentState }) => {
  if (!['revisionInvited', 'invited'].includes(currentState)) {
    if (consentToReInvite === true) {
      return (
        <Tooltip
          placement="top"
          label="Consented to review a revised version"
          shouldWrapChildren
        >
          <FontAwesomeIcon
            icon={faMemoCircleCheck}
            size="sm"
            cursor={'pointer'}
            style={{ color: 'green' }}
          />
        </Tooltip>
      );
    }

    if (consentToReInvite === false) {
      return (
        <Tooltip
          placement="top"
          label="Declined to review a revised version"
          shouldWrapChildren
        >
          <FontAwesomeIcon
            icon={faFileCircleXmark}
            size="sm"
            cursor={'pointer'}
            style={{ color: 'red' }}
          />
        </Tooltip>
      );
    }
  }
  return null;
};

const REVIEW_TABLE_COLUMNS = (
  selectedReviewers: string[],
  handleSelectReviewer: (e: React.ChangeEvent<HTMLInputElement>) => void,
  isRevisionRound: boolean,
  onDeclineReviewReason?: (reviewId: string) => void
): TableColumn[] => [
  {
    key: 'select',
    label: '',
    size: 0.3,
    render: review => (
      <Checkbox
        id={review.id}
        name={review.id}
        ariaLabel="selectAll"
        value={review.reviewer.email}
        isChecked={selectedReviewers.includes(review.reviewer.email)}
        onChange={e => handleSelectReviewer(e)}
      >
        {''}
      </Checkbox>
    )
  },
  {
    key: 'reviewer',
    label: 'Reviewer',
    size: 2,
    render: (review, onSelectedAuthor, reviewerData) => (
      <Flex flexDirection="column" alignItems="flex-start" gap={2}>
        <Box>
          <Flex align={'center'} gap={2}>
            <Box
              onClick={() => onSelectedAuthor(review?.reviewer)}
              cursor="pointer"
            >
              <UserName
                firstName={getReviewerNameOrEmail(review.reviewer)}
                enableIcon={
                  Object.hasOwn(reviewerData?.user ?? {}, 'isEmailDelivered') &&
                  reviewerData?.user?.isEmailDelivered === false
                }
                lastSentStatus={reviewerData?.user?.lastSentEmailStatus}
                isEmailDelivered={reviewerData?.user?.isEmailDelivered}
                timeStamp={reviewerData?.user?.lastSentEmailTimeStamp}
                isUserDeactivated={review.isUserDeactivated}
                tooltipLabel={'View Reviewer Profile'}
              />
            </Box>
            <ConsentToReInviteIcon
              consentToReInvite={resolveRoundConsentToReInvite(
                review,
                isRevisionRound
              )}
              currentState={review.workflow.currentState}
            />
            {review.isBoardMember && (
              <Tooltip placement="top" label="Board Member" shouldWrapChildren>
                <Badge variant="neutral" cursor={'pointer'} size="context">
                  <FontAwesomeIcon size="sm" icon={faUserTag} />
                </Badge>
              </Tooltip>
            )}
            <Tooltip
              hasArrow
              shouldWrapChildren
              placement="top"
              label={
                review.isUserDeactivated ? 'User Deactivated' : 'Proxy user'
              }
              borderRadius={4}
            >
              <ProxyUser
                isDecisionMaker
                user={{
                  userId: review?.reviewer?.id || '',
                  firstName: review?.reviewer?.firstName || '',
                  lastName: review?.reviewer?.lastName || '',
                  isUserDeactivated: review?.isUserDeactivated || false
                }}
                role={'reviewer'}
                reviewId={review.id}
                authorData={reviewerData}
              />
            </Tooltip>
          </Flex>
          <Text textStyle="textContext">
            {review.reviewer.affiliationDetails?.organizationName}
          </Text>
        </Box>

        {review.coReview?.status === 'accepted' && (
          <>
            <Box>
              <Text textStyle="headingItem">
                {getReviewerNameOrEmail(review.coReview.coReviewer)}
              </Text>
              {review.coReview.coReviewer.affiliation && (
                <Text textStyle="textContext">
                  {review.coReview.coReviewer.affiliation}
                </Text>
              )}
              <Badge variant="attention" size="context">
                <FontAwesomeIcon icon={faPeopleArrows} />
                Co-Reviewer
              </Badge>
            </Box>
          </>
        )}
      </Flex>
    )
  },
  {
    key: 'status',
    label: 'Status',
    size: 1,
    render: review => renderReviewStatusColumn(review, onDeclineReviewReason)
  },
  {
    key: 'deadline',
    label: 'Invite/Deadline',
    size: 1,
    render: renderDeadlineColumn
  },
  {
    key: 'recommendation',
    label: 'Recommendation/Score',
    size: 1,
    render: review => (
      <Flex direction="column" gap={1}>
        <Tooltip
          label={
            renderRecommendationColumn(review) !== 'N/A'
              ? 'View Recommendation'
              : undefined
          }
          placement="top"
          hasArrow
        >
          <Box>{renderRecommendationColumn(review)}</Box>
        </Tooltip>
        <Flex align="center" gap={1}>
          <Text textStyle="textContext">Score:</Text>
          {renderReviewScoreColumn(review)}
        </Flex>
      </Flex>
    )
  }
];

const TableHeader = ({
  columns,
  handleSelectAll,
  isAllSelected
}: {
  columns: TableColumn[];
  handleSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAllSelected: boolean;
}) => (
  <Table.HeaderRow>
    {columns.map(column => (
      <Table.HeaderCell
        key={column.key}
        flex={column.size}
        textAlign="left"
        whiteSpace={column.key === 'recommendation' ? 'normal' : 'nowrap'}
        wordBreak={column.key === 'recommendation' ? 'break-word' : 'normal'}
      >
        {column.key === 'select' ? (
          <Checkbox
            name="selectAll"
            id="select-all"
            ariaLabel="Select All"
            onChange={e => handleSelectAll(e)}
            isChecked={isAllSelected}
          >
            {''}
          </Checkbox>
        ) : column.key === 'recommendation' ? (
          <>
            {column.label}
            <Tooltip
              label="Score attributed by the Editor to this review"
              aria-label="Review Score Info"
              placement="top"
              hasArrow
              shouldWrapChildren
              data-cy="review-and-score-tooltip"
            >
              <ButtonIcon
                size="small"
                ariaLabel="Review and Score Info Icon"
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                data-cy="review-and-score-info-icon"
              />
            </Tooltip>
          </>
        ) : (
          column.label
        )}
      </Table.HeaderCell>
    ))}
    <Table.HeaderCell key="actions" width={10} textAlign="center" />
  </Table.HeaderRow>
);

interface RevisionReviewRowProps {
  review: IPeerReviewListItem;
  index: number;
  isLastRow: boolean;
  tableColumns: TableColumn[];
  isReviewed: boolean;
  onSelectReviewId: (reviewId: string) => void;
  onSelectedAuthor: (author: IReviewer | null) => void;
  onGrantExtension: (reviewId: string) => void;
  onRateReview: (reviewId: string) => void;
  onReinviteReviewer: (reviewId: string) => void;
  onDeclineReviewReason: (reviewId: string) => void;
  onResendInvite: (reviewId: string) => void;
  onWithdrawReviewer: (
    reviewId: string,
    reviewer: IReviewer,
    actionName: IPeerReviewWithdrawActions
  ) => void;
  onSendEmail: (modal: AdHocModalOpen) => void;
  onRescindReview: (reviewId: string) => void;
  onReassignBoardMember?: (reviewId: string) => void;
}

const RevisionReviewRow: React.FC<RevisionReviewRowProps> = ({
  review,
  index,
  isLastRow,
  tableColumns,
  isReviewed,
  onSelectReviewId,
  onSelectedAuthor,
  onGrantExtension,
  onRateReview,
  onReinviteReviewer,
  onDeclineReviewReason,
  onResendInvite,
  onWithdrawReviewer,
  onSendEmail,
  onRescindReview,
  onReassignBoardMember
}) => {
  const orgId = getOrgId();
  const id = review.reviewer.id;
  const { data: authorData } = useQuery({
    queryKey: ['workspace-author-profile', orgId, id],
    queryFn: () => fetchAuthorProfileData(orgId, id),
    enabled: Boolean(orgId && id)
  });

  const isRecommendationAvailable =
    isReviewed &&
    review.workflow.currentState !== 'awaitingRevisionReviewRequest' &&
    Boolean(review.caseData.revisionAssessment?.recommendation);

  return (
    <Table.Row
      key={index}
      style={{
        cursor: isRecommendationAvailable ? 'pointer' : 'default',
        borderBottom: isLastRow ? 'none' : undefined
      }}
    >
      {tableColumns.map(column => (
        <Table.RowCell
          key={column.key}
          flex={column.size}
          textAlign="left"
          px={column.key === 'select' ? 0 : 3}
          py={2}
          _hover={{
            bg:
              column.key === 'recommendation' && isRecommendationAvailable
                ? 'gray.50'
                : undefined
          }}
          onClick={() => {
            if (column.key === 'recommendation') {
              isRecommendationAvailable && onSelectReviewId(review.id);
            }
          }}
          height={'full'}
        >
          {column.render
            ? column.render(review, onSelectedAuthor, authorData)
            : null}
        </Table.RowCell>
      ))}
      <Table.RowCell key="actions" width={10}>
        {renderActionsColumn(
          review,
          onSelectReviewId,
          onSelectedAuthor,
          onGrantExtension,
          onRateReview,
          onReinviteReviewer,
          onDeclineReviewReason,
          onResendInvite,
          onWithdrawReviewer,
          onSendEmail,
          onRescindReview,
          review.isUserDeactivated,
          onReassignBoardMember
        )}
      </Table.RowCell>
    </Table.Row>
  );
};

const RevisionReviewsTable = ({
  reviews,
  onSelectReviewId,
  onSelectedAuthor,
  onRateReview,
  onDeclineReviewReason,
  onGrantExtension,
  onReinviteReviewer,
  onResendInvite,
  onWithdrawReviewer,
  onSendEmail,
  onRescindReview,
  onReassignBoardMember
}: RevisionReviewTableProps) => {
  const [selectedReviewers, setSelectedReviewers] = useState<string[]>([]);
  const {
    setSearchParams,
    searchParams: appliedSearch,
    status,
    setStatus,
    sort,
    setSort
  } = useSubmissionReviewsData();
  // Opening a reviewer profile or a review replaces this table and coming back
  // remounts it, so start the box off with the search that is still applied.
  const [searchReviewer, setSearchReviewer] = useState<string>(appliedSearch);

  const handleSelectReviewer = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedReviewers([...selectedReviewers, e.target.value]);
    } else {
      const filteredReviewers = selectedReviewers.filter(
        email => email !== e.target.value
      );
      setSelectedReviewers(filteredReviewers);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedReviewers(reviews.map(review => review.reviewer.email));
    } else {
      setSelectedReviewers([]);
    }
  };

  const isAllSelected = reviews.length === selectedReviewers.length;

  const { hasSubmittedReviewInCurrentRound, isRevisionRound } =
    useSubmissionReviewsData();
  const tableColumns = REVIEW_TABLE_COLUMNS(
    selectedReviewers,
    handleSelectReviewer,
    isRevisionRound,
    onDeclineReviewReason
  );

  const { data } = useQuery({
    queryKey: ['reviewer-status'],
    queryFn: fetchReviewerStatus,
    select: response => response.data
  });
  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
  };

  const handleSortReset = () => {
    setSort(null);
  };
  return (
    <>
      <Flex gap={2}>
        <Box flex="0 0 30% 30%">
          <Flex gap={2} alignItems="center">
            <SearchInput
              placeholder="Search"
              width="100%"
              iconLeft={<FontAwesomeIcon icon={faSearch} />}
              aria-label="Search Reviewers"
              name="searchReviewers"
              value={searchReviewer}
              enterKeyHint="search"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchReviewer(e.target.value)
              }
              onInputClear={() => {
                setSearchReviewer('');
                setSearchParams('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setSearchParams(searchReviewer);
                }
              }}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setSearchParams(searchReviewer);
                }
              }}
            />
            <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
              <Button
                variant="primary"
                aria-label="Search"
                onClick={() => setSearchParams(searchReviewer)}
              >
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </Box>
          </Flex>
        </Box>
        <Select
          value={status[0]}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setStatus([e.target.value])
          }
          placeholder="Filter by status: All"
          flex="0 0 25%"
        >
          {data?.filters[0]?.items.map(filter => (
            <option value={filter.value}>{filter.name}</option>
          ))}
        </Select>
        {data?.sortOptions && (
          <Box width={{ base: '100%', lg: 'auto' }}>
            <SortMenu
              appliedSort={sort}
              options={data.sortOptions}
              onApply={handleSortApply}
              onReset={handleSortReset}
            />
          </Box>
        )}
        <Menu ariaLabel="All Queue Actions">
          <MenuButton
            as={Button}
            rightIcon={<FontAwesomeIcon icon={faEllipsis} />}
            variant="secondary"
            disabled={!selectedReviewers.length}
            flex="0 0 15%"
          >
            Actions
          </MenuButton>
          <MenuList>
            <MenuItem
              ariaLabel="Invite"
              onClick={() =>
                onSendEmail({
                  status: true,
                  defaultEmail: selectedReviewers
                })
              }
            >
              Send Email
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      {reviews.length === 0 ? (
        // Empty means the filters matched nothing, so the table — header
        // included — is left out rather than drawn around a single empty row.
        <Flex direction="column" align="center" gap={2} py={8}>
          <Text textStyle="headingItem">No results found</Text>
          <Text textStyle="textContext">
            Try adjusting your search or filters.
          </Text>
        </Flex>
      ) : (
        <Table>
          <TableHeader
            columns={tableColumns}
            handleSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
          />
          <Table.Body>
            {reviews.map((review, index) => (
              <RevisionReviewRow
                key={index}
                review={review}
                index={index}
                isLastRow={index === reviews.length - 1}
                tableColumns={tableColumns}
                isReviewed={hasSubmittedReviewInCurrentRound(review.id)}
                onSelectReviewId={onSelectReviewId}
                onSelectedAuthor={onSelectedAuthor}
                onGrantExtension={onGrantExtension}
                onRateReview={onRateReview}
                onReinviteReviewer={onReinviteReviewer}
                onDeclineReviewReason={onDeclineReviewReason}
                onResendInvite={onResendInvite}
                onWithdrawReviewer={onWithdrawReviewer}
                onSendEmail={onSendEmail}
                onRescindReview={onRescindReview}
                onReassignBoardMember={onReassignBoardMember}
              />
            ))}
          </Table.Body>
        </Table>
      )}
    </>
  );
};

export default RevisionReviewsTable;
